import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  useLazyGetUserByIdQuery,
  useExpireUserSubscriptionMutation,
  useCancelUserSubscriptionMutation,
} from "../../../services/users";
import MainContainer from "../../../layout/MainContainer";
import "../Users.scss";
import { showError, showToast } from "../../../constants";
import useTranslation from "../../../hooks/Translation";
import NoDataFound from "../../../components/NoDataFound";
import moment from "moment";
import { formatPhoneLtr } from "../../../utils/phoneDisplay";
import DemoToolsPanel from "../../../components/DemoToolsPanel";

const subscriptionStatusLabel = (status?: number) => {
  switch (Number(status)) {
    case 0:
      return "Cancelled";
    case 1:
      return "Active";
    case 2:
      return "Expired";
    default:
      return "-";
  }
};

const subscriptionStatusColor = (
  status?: number,
): "success" | "warning" | "error" | "default" => {
  switch (Number(status)) {
    case 0:
      return "error";
    case 1:
      return "success";
    case 2:
      return "warning";
    default:
      return "default";
  }
};

const paymentTypeLabel = (type?: number) => {
  switch (Number(type)) {
    case 1:
      return "Wallet Top-up";
    case 2:
      return "Subscription";
    case 3:
      return "Request to Agent";
    default:
      return "-";
  }
};

const paymentStatusLabel = (status?: number | string) => {
  switch (Number(status)) {
    case 1:
      return "Pending";
    case 2:
      return "Completed";
    case 3:
      return "Cancelled";
    case 4:
      return "Refunded";
    default:
      return status || "-";
  }
};

const statusColor = (status?: number | string) => {
  const value = Number(status);
  if (value === 1 || value === 2) return "success";
  if (value === 4) return "info";
  return "default";
};

const UserDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [getUserData] = useLazyGetUserByIdQuery();
  const [expireUserSubscription] = useExpireUserSubscriptionMutation();
  const [cancelUserSubscription] = useCancelUserSubscriptionMutation();
  const [useDetails, setUserDetails] = useState<any>();
  const [planActionDialog, setPlanActionDialog] = useState<{
    open: boolean;
    action: "expire" | "cancel" | null;
    plan: any;
  }>({ open: false, action: null, plan: null });
  const [planActionLoading, setPlanActionLoading] = useState(false);
  const translate = useTranslation();
  const getUserDetailById = async (id: string | undefined) => {
    try {
      const response = await getUserData({ user_id: id }).unwrap();
      if (response?.statusCode === 200) {
        setUserDetails(response?.data);
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const handlePlanActionConfirm = async () => {
    if (!id || !planActionDialog.plan?._id || !planActionDialog.action) return;
    setPlanActionLoading(true);
    try {
      const payload = {
        userId: id,
        subscriptionId: String(planActionDialog.plan._id),
      };
      const response =
        planActionDialog.action === "expire"
          ? await expireUserSubscription(payload).unwrap()
          : await cancelUserSubscription(payload).unwrap();
      if (response?.statusCode === 200) {
        showToast(
          planActionDialog.action === "expire"
            ? "Plan expired successfully"
            : "Plan cancelled successfully",
        );
        setPlanActionDialog({ open: false, action: null, plan: null });
        getUserDetailById(id);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    } finally {
      setPlanActionLoading(false);
    }
  };

  useEffect(() => {
    getUserDetailById(id);
  }, [id]);

  return (
    <MainContainer>
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.user?.ViewUserProfile}</h1>
          <Button
            className="btn btn_primary"
            onClick={() => {
              navigate("/manageUsers");
            }}
          >
            {translate?.main?.Back}
          </Button>
        </div>
        <Card className="cards">
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={2} className="view_box">
              <Grid item xs={10} className="view_box_list">
                <Grid
                  item
                  lg={2}
                  md={2}
                  sm={6}
                  xs={12}
                  sx={{ paddingBottom: 5 }}
                >
                  <figure className="profile_img">
                    <img
                      src={
                        useDetails?.image
                          ? useDetails?.image
                          : "/static/images/user_placeholder.png"
                      }
                      alt=""
                    />
                  </figure>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">
                        {" "}
                        {translate?.main?.Name}
                      </Typography>
                      <Typography component="p">
                        {useDetails?.fullName || "-"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">
                        {translate?.main?.PhoneNo}
                      </Typography>
                      <Typography component="p">
                        {formatPhoneLtr(
                          useDetails?.countryCode,
                          useDetails?.phone
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">
                        {translate?.main?.Email}
                      </Typography>
                      <Typography component="p">
                        {useDetails?.email || "-"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item lg={3} md={3} sm={6} xs={12}>
                    <Box>
                      <Typography component="h5">
                        {translate?.main?.AcountStatus}
                      </Typography>
                      <Typography component="p">
                        {useDetails?.isBlocked == false ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item mt={3} xs={12} className="view_box_list">
                    <Typography
                      className="detail_title mn_hdng"
                      component="h2"
                      mb={3}
                    >
                      {translate?.main?.NormalListingCredits}
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {translate?.main?.TotalCredits}
                          </Typography>
                          <Typography component="p">
                            {" "}
                            {useDetails?.normalCred || "0"}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* <Grid item xs={3}>
                        <Box>
                          <Typography component="h5">
                          {translate?.main?.Totalcreditsused}
                          </Typography>
                          <Typography component="p">-</Typography>
                        </Box>
                      </Grid>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                          {translate?.main?.TotalCreditsLeft}
                          </Typography>
                          <Typography component="p">-</Typography>
                        </Box>
                      </Grid> */}
                    </Grid>
                  </Grid>
                  <Grid item mt={3} xs={12} className="view_box_list">
                    <Typography
                      className="detail_title mn_hdng"
                      component="h2"
                      mb={3}
                    >
                      {translate?.main?.HighlightedListingCredits}
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                            {" "}
                            {translate?.main?.TotalCredits}
                          </Typography>
                          <Typography component="p">
                            {useDetails?.highlightCred || "0"}
                          </Typography>
                        </Box>
                      </Grid>
                      {/* <Grid item xs={3}>
                        <Box>
                          <Typography component="h5">
                          {translate?.main?.Totalcreditsused}
                          </Typography>
                          <Typography component="p">-</Typography>
                        </Box>
                      </Grid>
                      <Grid item lg={3} md={3} sm={6} xs={12}>
                        <Box>
                          <Typography component="h5">
                          {translate?.main?.TotalCreditsLeft}
                          </Typography>
                          <Typography component="p">-</Typography>
                        </Box>
                      </Grid> */}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 4 }} className="cards">
          <CardContent>
            <Typography className="detail_title mn_hdng" component="h2" mb={3}>
              Subscription Plan History
            </Typography>
            <TableContainer className="table_container">
              <Table sx={{ minWidth: 1000 }} aria-label="subscription history">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Purchased</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Credits Granted</TableCell>
                    <TableCell>Credits Remaining</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {useDetails?.subscriptionHistory?.length ? (
                    useDetails.subscriptionHistory.map((row: any, index: number) => (
                      <TableRow key={row?._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {row?.planName || row?.membershipPlan?.planName || "-"}
                        </TableCell>
                        <TableCell>
                          {Number.isFinite(Number(row?.price))
                            ? `${Number(row.price).toLocaleString("en-IN")} KD`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.createdAt
                            ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.expiryDate
                            ? moment(row.expiryDate).format("DD MMM YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={subscriptionStatusLabel(row?.status)}
                            color={subscriptionStatusColor(row?.status)}
                          />
                        </TableCell>
                        <TableCell>
                          Normal: {row?.normalCredit ?? 0}
                          <br />
                          Highlight: {row?.highlightCredit ?? 0}
                          <br />
                          Requests: {row?.acceptRequests ?? 0}
                        </TableCell>
                        <TableCell>
                          Normal: {row?.normalCreditLeft ?? 0}
                          <br />
                          Highlight: {row?.highlightCreditLeft ?? 0}
                          <br />
                          Requests: {row?.acceptRequestsLeft ?? 0}
                        </TableCell>
                        <TableCell>{row?.transactionId || "-"}</TableCell>
                        <TableCell align="center">
                          {Number(row?.status) === 1 ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                disabled={!row?._id || planActionLoading}
                                onClick={() =>
                                  setPlanActionDialog({
                                    open: true,
                                    action: "expire",
                                    plan: row,
                                  })
                                }
                              >
                                Expire
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={!row?._id || planActionLoading}
                                onClick={() =>
                                  setPlanActionDialog({
                                    open: true,
                                    action: "cancel",
                                    plan: row,
                                  })
                                }
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <NoDataFound message="No subscription history found" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ mt: 4 }} className="cards">
          <CardContent>
            <Typography className="detail_title mn_hdng" component="h2" mb={3}>
              Payment History
            </Typography>
            <TableContainer className="table_container">
              <Table sx={{ minWidth: 1100 }} aria-label="payment history">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Payment For</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Provider Charge ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {useDetails?.paymentHistory?.length ? (
                    useDetails.paymentHistory.map((row: any, index: number) => {
                      const details =
                        Number(row?.paymentType) === 1
                          ? `Normal: ${row?.normalCreditsBought ?? 0}, Highlight: ${
                              row?.highlightCreditsBought ?? 0
                            }`
                          : Number(row?.paymentType) === 2
                            ? row?.subscription?.planName || "Subscription plan"
                            : row?.property?.title ||
                              row?.property?.propertyName ||
                              "Property request";

                      return (
                        <TableRow key={row?._id || index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{paymentTypeLabel(row?.paymentType)}</TableCell>
                          <TableCell>{details}</TableCell>
                          <TableCell>
                            {Number.isFinite(Number(row?.amount))
                              ? `${Number(row.amount).toLocaleString("en-IN")} ${
                                  row?.currency || "KWD"
                                }`
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {row?.createdAt
                              ? moment(row.createdAt).format(
                                  "DD MMM YYYY, hh:mm A",
                                )
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={paymentStatusLabel(row?.paymentStatus)}
                              color={statusColor(row?.paymentStatus) as any}
                            />
                          </TableCell>
                          <TableCell>{row?.paymentMode || "-"}</TableCell>
                          <TableCell>{row?.transactionId || "-"}</TableCell>
                          <TableCell>{row?.providerChargeId || "-"}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <NoDataFound message="No payment history found" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <DemoToolsPanel
          variant="user"
          entityId={id}
          context={{
            role: useDetails?.role,
            membershipBought: useDetails?.membershipBought,
            walletNormal: useDetails?.normalCred ?? 0,
            walletHighlight: useDetails?.highlightCred ?? 0,
            planNormalLeft:
              useDetails?.subscription?.normalCreditLeft ??
              useDetails?.normalCreditLeft ??
              "-",
            planHighlightLeft:
              useDetails?.subscription?.highlightCreditLeft ??
              useDetails?.highlightCreditLeft ??
              "-",
            planStatus: useDetails?.subscription?.status ?? useDetails?.membershipStatus,
            planExpiry: useDetails?.subscription?.expiryDate
              ? moment(useDetails.subscription.expiryDate).format(
                  "DD MMM YYYY, hh:mm A",
                )
              : useDetails?.expiryDate
                ? moment(useDetails.expiryDate).format("DD MMM YYYY, hh:mm A")
                : null,
            creditExpiry: useDetails?.subscription?.creditExpiryDate
              ? moment(useDetails.subscription.creditExpiryDate).format(
                  "DD MMM YYYY, hh:mm A",
                )
              : null,
          }}
          onSuccess={() => getUserDetailById(id)}
        />

        <Dialog
          open={planActionDialog.open}
          onClose={() => {
            if (!planActionLoading) {
              setPlanActionDialog({ open: false, action: null, plan: null });
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {planActionDialog.action === "expire" ? "Expire Plan" : "Cancel Plan"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {planActionDialog.action === "expire"
                ? `Expire "${planActionDialog.plan?.planName || "this plan"}"? Remaining credits on this plan will move to the next active plan and stay valid until that plan ends.`
                : `Cancel "${planActionDialog.plan?.planName || "this plan"}"? Remaining credits on this plan will move to the next active plan and stay valid until that plan ends.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              disabled={planActionLoading}
              onClick={() =>
                setPlanActionDialog({ open: false, action: null, plan: null })
              }
            >
              Close
            </Button>
            <Button
              variant="contained"
              color={planActionDialog.action === "expire" ? "warning" : "error"}
              disabled={planActionLoading}
              onClick={handlePlanActionConfirm}
            >
              {planActionLoading
                ? "Processing..."
                : planActionDialog.action === "expire"
                  ? "Confirm Expire"
                  : "Confirm Cancel"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </MainContainer>
  );
};

export default UserDetails;
